import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "./LoginPage.scss";
import { loginUser } from "@/api/user";
import { Link, useNavigate } from "react-router-dom";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { ReactComponent as TieOutLogo } from "@/assets/icons/tie-out.svg";
import { ReactComponent as LockIcon } from "@/assets/icons/lock.svg";
import { ReactComponent as EyeIcon } from "@/assets/icons/eye.svg";
import { ReactComponent as EyeOffIcon } from "@/assets/icons/eye-off.svg";

type FormValues = {
  email: string;
  password: string;
  rememberMe: boolean;
};

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { rememberMe: false },
  });

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    loginUser(data.email, data.password, data.rememberMe)
      .then((response) => {
        localStorage.setItem("token", response.data.access_token);
        reset();
        setErrorMessage(undefined);
        navigate("/dashboard");
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          setErrorMessage(t("pages.auth.login.errors.invalidCredentials"));
          return;
        }
        setErrorMessage(t("errors.generic"));
      })
      .finally(() => {});
  };

  return (
    <div className="login-page">
      <TieOutLogo className="login-logo" />

      <div className="login-card">
        <h1 className="login-title">
          {t("pages.auth.login.titleLine1")}
          <br />
          <span className="login-title-brand">
            {t("pages.auth.login.titleLine2")}
          </span>
        </h1>

        <p className="login-subtitle">{t("pages.auth.login.subtitle")}</p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="login-form"
          noValidate
        >
          <div className="login-field">
            <label className="login-label" htmlFor="login-email">
              {t("pages.auth.login.fields.email")}
            </label>
            <input
              id="login-email"
              className="login-input"
              type="email"
              autoComplete="email"
              placeholder={t("pages.auth.login.fields.emailPlaceholder")}
              {...register("email", { required: true })}
            />
            {errors.email && (
              <span className="login-error">
                {t("pages.auth.login.errors.emailRequired")}
              </span>
            )}
          </div>

          <div className="login-field">
            <div className="login-label-row">
              <label className="login-label" htmlFor="login-password">
                {t("pages.auth.login.fields.password")}
              </label>
              <Link className="login-forgot-link" to="/forgot-password">
                {t("pages.auth.login.links.forgotPassword")}
              </Link>
            </div>

            <div className="login-password-wrap">
              <LockIcon className="login-password-left-icon" aria-hidden />
              <input
                id="login-password"
                className="login-input login-password-input"
                type={isPasswordVisible ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("pages.auth.login.fields.passwordPlaceholder")}
                {...register("password", { required: true })}
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={t("pages.auth.login.actions.togglePasswordVisibility")}
              >
                {isPasswordVisible ? (
                  <EyeOffIcon className="login-password-toggle-icon" aria-hidden />
                ) : (
                  <EyeIcon className="login-password-toggle-icon" aria-hidden />
                )}
              </button>
            </div>
            {errors.password && (
              <span className="login-error">
                {t("pages.auth.login.errors.passwordRequired")}
              </span>
            )}
          </div>

          <div className="login-field login-remember-row">
            <label className="login-remember-label" htmlFor="login-remember">
              <input
                id="login-remember"
                type="checkbox"
                {...register("rememberMe")}
              />
              <span>{t("pages.auth.login.fields.rememberMe")}</span>
            </label>
          </div>

          {errorMessage && <p className="login-error-message">{errorMessage}</p>}

          <div className="login-submit">
            <GenericButton label={t("pages.auth.login.actions.login")} />
          </div>

          <a
            className="login-request-access"
            href="https://www.tie-out.ai/contact"
            target="_blank"
            rel="noreferrer"
          >
            {t("pages.auth.login.footer.requestAccess")}
          </a>
        </form>
      </div>
    </div>
  );
};
